import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookmarksApi } from '@/api/endpoints/bookmarks';
import type { VideoReference } from '@/api/types';
import { useToast } from '@/app/toasts/ToastProvider';
import { useAuth } from '@/auth/AuthProvider';
import { useT } from '@/i18n/LocaleProvider';
import { bookmarksQueryKey } from './useBookmarks';

const savedVideosKey = [...bookmarksQueryKey, 'videos'] as const;

/** Видео из закладок для страницы Saved; ключ под общим префиксом закладок — инвалидируется вместе с ними. */
export function useSavedVideos() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: savedVideosKey,
    queryFn: () => bookmarksApi.videos(),
    enabled: isAuthenticated,
    select: (response) => response.data,
  });
}

/**
 * Своё название закладки (видно только в Saved): карточка меняется сразу, ошибка
 * возвращает прежнее название и показывает тост.
 */
export function useSavedTitle() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const t = useT();
  return useMutation({
    mutationFn: ({ videoId, title }: { videoId: number; title: string | null }) =>
      bookmarksApi.setTitle(videoId, title),
    onMutate: async ({ videoId, title }) => {
      await queryClient.cancelQueries({ queryKey: savedVideosKey });
      const previous = queryClient.getQueryData<{ data: VideoReference[] }>(savedVideosKey);
      queryClient.setQueryData<{ data: VideoReference[] }>(savedVideosKey, (current) =>
        current
          ? {
              ...current,
              data: current.data.map((v) => (v.id === videoId ? { ...v, saved_title: title } : v)),
            }
          : current,
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(savedVideosKey, context.previous);
      toast.show(t('library.renameFailed'));
    },
  });
}
